import { writable } from "svelte/store";
import { io } from "socket.io-client";
import { API_URL } from "@/api";

export interface DashboardInvalidation {
  account?: true;
  servers?: true;
  serverIds?: string[];
  allServers?: true;
  capacity?: true;
}
interface Cursor {
  streamId: string;
  sequence: number;
}
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const full: DashboardInvalidation = {
  account: true,
  servers: true,
  allServers: true,
  capacity: true,
};
export const dashboardRealtimeHealthy = writable(false);
export const dashboardRealtimeSubscribed = writable(false);
type Observer = (event: DashboardInvalidation) => void | Promise<void>;
const observers = new Set<Observer>();

export function subscribeDashboardInvalidations(observer: Observer): () => void {
  observers.add(observer);
  return () => observers.delete(observer);
}

function cursor(value: unknown): Cursor | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const item = value as Record<string, unknown>;
  if (
    item.contractVersion !== 1 ||
    typeof item.streamId !== "string" ||
    !uuid.test(item.streamId)
  ) {
    return null;
  }
  if (!Number.isSafeInteger(item.sequence) || (item.sequence as number) < 0) {
    return null;
  }
  return { streamId: item.streamId, sequence: item.sequence as number };
}

function invalidation(value: unknown): DashboardInvalidation | null {
  if (!cursor(value)) {
    return null;
  }
  const item = value as Record<string, unknown>;
  const result: DashboardInvalidation = {};
  for (const key of ["account", "servers", "allServers", "capacity"] as const) {
    if (item[key] !== undefined && typeof item[key] !== "boolean") {
      return null;
    }
    if (item[key] === true) {
      result[key] = true;
    }
  }
  if (item.serverIds !== undefined) {
    if (
      !Array.isArray(item.serverIds) ||
      item.serverIds.length > 32 ||
      item.serverIds.some((id) => typeof id !== "string" || !uuid.test(id))
    ) {
      return null;
    }
    result.serverIds = item.serverIds as string[];
  }
  return result;
}

function merge(
  before: DashboardInvalidation | null,
  next: DashboardInvalidation,
): DashboardInvalidation {
  const result = { ...before, ...next };
  const ids = new Set([...(before?.serverIds ?? []), ...(next.serverIds ?? [])]);
  if (result.allServers || ids.size > 32) {
    result.allServers = true;
    delete result.serverIds;
  } else if (ids.size) {
    result.serverIds = [...ids];
  }
  return result;
}

/** Subscribe first, then reconcile snapshots. Events during reads become one follow-up. */
export function startDashboardRealtime(token: string): () => void {
  let stopped = false;
  let generation = 0;
  let active: Cursor | null = null;
  let pending: DashboardInvalidation | null = null;
  let delivering = false;
  let subscribed = false;
  let retryAttempt = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let deliveryRetry: ReturnType<typeof setTimeout> | null = null;
  const socket = io(`${API_URL}/dashboard`, {
    auth: { token },
    transports: ["websocket"],
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 30_000,
    randomizationFactor: 0.5,
  });
  dashboardRealtimeHealthy.set(false);
  const clearRetry = () => {
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
    }
    retryTimer = null;
  };
  const deliver = async () => {
    const inactive = stopped || document.hidden || !subscribed;
    const deliveryBlocked = !inactive && (delivering || deliveryRetry !== null || !pending);
    if (inactive || deliveryBlocked) {
      return;
    }
    delivering = true;
    const version = generation;
    try {
      while (!stopped && !document.hidden && subscribed) {
        if (generation !== version || !pending) {
          break;
        }
        const event = pending;
        pending = null;
        const results = await Promise.allSettled(
          [...observers].map((observer) => Promise.resolve().then(() => observer(event))),
        );
        if (stopped || generation !== version) {
          break;
        }
        if (results.some((result) => result.status === "rejected")) {
          pending = merge(pending, full);
          dashboardRealtimeHealthy.set(false);
          deliveryRetry = setTimeout(
            () => {
              deliveryRetry = null;
              void deliver();
            },
            2_000 + Math.floor(Math.random() * 1_000),
          );
          return;
        }
      }
      const subscriptionStillCurrent = !stopped && subscribed && generation === version;
      if (subscriptionStillCurrent && !pending) {
        dashboardRealtimeHealthy.set(true);
      }
    } finally {
      delivering = false;
      const hasPendingSubscription = !stopped && subscribed && pending;
      if (hasPendingSubscription && deliveryRetry === null) {
        void deliver();
      }
    }
  };
  const suspend = () => {
    if (deliveryRetry !== null) {
      clearTimeout(deliveryRetry);
    }
    deliveryRetry = null;
    generation++;
    active = null;
    subscribed = false;
    dashboardRealtimeSubscribed.set(false);
    pending = null;
    dashboardRealtimeHealthy.set(false);
  };
  const retry = (retryAfterSec?: unknown) => {
    suspend();
    if (stopped || retryTimer !== null) {
      return;
    }
    const backoff = Math.min(25_000, 1_000 * 2 ** Math.min(retryAttempt++, 5));
    const requested =
      typeof retryAfterSec === "number" && Number.isFinite(retryAfterSec) && retryAfterSec > 0
        ? Math.min(30_000, retryAfterSec * 1_000)
        : 0;
    const delay = Math.max(backoff, requested);
    retryTimer = setTimeout(
      () => {
        retryTimer = null;
        if (socket.disconnected) {
          socket.connect();
        } else {
          subscribe();
        }
      },
      Math.min(30_000, delay + Math.floor(Math.random() * delay * 0.2)),
    );
  };
  const fatal = () => {
    stopped = true;
    suspend();
    clearRetry();
    socket.disconnect();
  };
  const subscribe = () => {
    suspend();
    clearRetry();
    const version = generation;
    socket.timeout(10_000).emit("subscribe", {}, (error: unknown, reply: unknown) => {
      if (stopped || generation !== version) {
        return;
      }
      const item = reply as { ok?: boolean; retryable?: boolean; retryAfterSec?: number } | null;
      const next = cursor(reply);
      if (error || !item?.ok || !next) {
        if (!error && item?.retryable === false) {
          fatal();
        } else {
          retry(item?.retryAfterSec);
        }
        return;
      }
      active = next;
      subscribed = true;
      dashboardRealtimeSubscribed.set(true);
      retryAttempt = 0;
      // All pre-ACK hints are covered by this snapshot; subsequent hints queue behind it.
      pending = full;
      void deliver();
    });
  };
  socket.on("connect", subscribe);
  socket.on("disconnect", (reason: string) => {
    suspend();
    if (reason === "io server disconnect") {
      retry();
    }
  });
  socket.on("connect_error", () => retry());
  socket.on("auth-error", (error: { retryable?: boolean; retryAfterSec?: number }) => {
    if (error?.retryable) {
      retry(error.retryAfterSec);
    } else {
      fatal();
    }
  });
  socket.on("invalidate", (value: unknown) => {
    const next = cursor(value);
    const event = invalidation(value);
    if (!next || !event) {
      retry();
      return;
    }
    if (!subscribed || !active) {
      return;
    } // The post-ACK snapshot covers this notification.
    if (next.streamId !== active.streamId) {
      // Only an explicit complete reconciliation may introduce a new local stream.
      const reconcilesCompleteDashboard =
        event.account && event.servers && event.allServers && event.capacity;
      if (!reconcilesCompleteDashboard) {
        retry();
        return;
      }
      active = next;
      pending = merge(pending, full);
    } else {
      if (next.sequence <= active.sequence) {
        return;
      }
      pending = merge(pending, next.sequence === active.sequence + 1 ? event : full);
      active = next;
    }
    dashboardRealtimeHealthy.set(false);
    void deliver();
  });
  const visibility = () => {
    if (!document.hidden && subscribed) {
      dashboardRealtimeHealthy.set(false);
      pending = merge(pending, full);
      void deliver();
    }
  };
  document.addEventListener("visibilitychange", visibility);
  return () => {
    stopped = true;
    suspend();
    clearRetry();
    if (deliveryRetry !== null) {
      clearTimeout(deliveryRetry);
    }
    document.removeEventListener("visibilitychange", visibility);
    socket.removeAllListeners();
    socket.disconnect();
  };
}
